import React, { useState, useEffect, useMemo } from 'react';
import { 
  MagnifyingGlassIcon,
  XMarkIcon,
  AcademicCapIcon,
  ChevronRightIcon,
  StarIcon,
  ClockIcon,
  ChartBarIcon,
  BookOpenIcon,
  TrophyIcon,
  MicrophoneIcon,
  ClockIcon as HistoryIcon,
  FireIcon,
  SparklesIcon,
  EyeIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { examData, getDynamicExamData } from '../data/examData';
import { convertQNetToExamFormat, createSlugFromName } from '../utils/qnetUtils';
import { getPopularCertificationsTop10, getPopularCertificationsByLevel, loadPassRatesData } from '../utils/popularCertificationsUtil';
import { useAuth } from '../contexts/AuthContext';
import { autoFeedbackActions } from '../utils/autoFeedbackTrigger';
import { gamificationActions } from '../utils/gamificationSystem';
import Header from './common/Header';
import LoginPromptManager from './auth/LoginPromptManager';

const ExamSearchSelection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [dynamicExamData, setDynamicExamData] = useState(null);
  const [qnetData, setQnetData] = useState([]);
  const [allExams, setAllExams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredExams, setFilteredExams] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [trendingSearches] = useState(['전기기사', '정보처리기사', '건축기사', '소방설비기사']);
  const [searchHistory, setSearchHistory] = useState([]);
  const [isVoiceSearch, setIsVoiceSearch] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [popularCertifications, setPopularCertifications] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [levelCertifications, setLevelCertifications] = useState({
    all: [],
    engineer: [],
    industrial: [],
    technician: []
  });
  
  const { user, isAuthenticated, logout, loginWithSocial } = useAuth();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Load dynamic exam data and Q-Net data
  useEffect(() => {
    const loadAllData = async () => {
      try {
        // Load CBT exam data
        const dynamicData = await getDynamicExamData();
        setDynamicExamData(dynamicData);
        console.log('🔄 ExamSearchSelection: Dynamic exam data loaded');

        // Load Q-Net certification data
        const qnetResponse = await fetch('/qnet-all-qualifications.json');
        const qnetCertifications = await qnetResponse.json();
        setQnetData(qnetCertifications);
        console.log('🔄 ExamSearchSelection: Q-Net data loaded:', qnetCertifications.length);

        // Combine both datasets for unified search
        const cbtExams = Object.keys(dynamicData).map(examKey => ({
          key: examKey,
          ...dynamicData[examKey],
          isCBTSupported: true,
          source: 'cbt'
        }));

        const qnetExams = qnetCertifications.map(cert => {
          const converted = convertQNetToExamFormat(cert);
          const slug = createSlugFromName(cert.jmfldnm);
          return {
            key: slug,
            title: cert.jmfldnm,
            ...converted,
            examCode: cert.jmcd,
            isCBTSupported: false,
            source: 'qnet'
          };
        });

        const combinedExams = [...cbtExams, ...qnetExams];
        setAllExams(combinedExams);
        console.log('🔄 Combined exams loaded:', combinedExams.length);
      } catch (error) {
        console.warn('ExamSearchSelection: Failed to load data, using static fallback:', error);
        setDynamicExamData(examData);
        // Fallback to CBT data only
        const fallbackExams = Object.keys(examData).map(examKey => ({
          key: examKey,
          ...examData[examKey],
          isCBTSupported: true,
          source: 'cbt'
        }));
        setAllExams(fallbackExams);
      }
    };
    
    loadAllData();

    // Load search history from localStorage
    const savedHistory = localStorage.getItem('examSearchHistory');
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory));
    }

    const savedRecent = localStorage.getItem('recentExamSearches');
    if (savedRecent) {
      setRecentSearches(JSON.parse(savedRecent));
    }
  }, []);



  // 베타 대시보드 제거

  // Real-time search suggestions with Q-Net integration
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchSuggestions([]);
      return;
    }

    const suggestions = [];
    
    // Search through combined exam data
    allExams.forEach(exam => {
      if (exam.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        suggestions.push({
          type: 'exam',
          title: exam.title,
          subtitle: exam.isCBTSupported ? 'CBT 지원' : '자격증 정보',
          icon: exam.isCBTSupported ? '💻' : '📜',
          examKey: exam.key
        });
      }
    });

    // Add subject matches for CBT exams
    allExams.filter(exam => exam.isCBTSupported).forEach(exam => {
      exam.subjects?.forEach(subject => {
        if (subject.toLowerCase().includes(searchQuery.toLowerCase()) && 
            !suggestions.find(s => s.title === subject)) {
          suggestions.push({
            type: 'subject',
            title: subject,
            subtitle: `${exam.title} 과목`,
            icon: '📚',
            examKey: exam.key
          });
        }
      });
    });

    setSearchSuggestions(suggestions.slice(0, 8));
  }, [searchQuery, allExams]);


  // Generate exam types from combined data - Memoized to prevent infinite re-renders
  const examTypes = useMemo(() => {
    return allExams.map(exam => {
      // 안전성 검사: exam 객체가 유효한지 확인
      if (!exam || typeof exam !== 'object') {
        console.warn(`Invalid exam data for key: ${exam?.key}`, exam);
        return null;
      }
      
      return {
        key: exam.key,
        title: exam.title,
        description: exam.description,
        questions: exam.isCBTSupported ? `${exam.totalQuestions}문제` : '정보 제공',
        timeLimit: exam.isCBTSupported ? `${exam.timeLimit}분` : '-',
        passingScore: exam.isCBTSupported ? `${exam.passingScore}점` : '-',
        popular: exam.popular,
        subjects: exam.subjects,
        totalQuestions: exam.totalQuestions || 0,
        isCBTSupported: exam.isCBTSupported,
        source: exam.source,
        category: exam.category,
        totalSessions: exam.years ? Object.values(exam.years).reduce((total, year) => {
          return total + Object.keys(year.sessions || {}).length;
        }, 0) : 0
      };
    }).filter(Boolean); // null 값 제거
  }, [allExams]); // Only recompute when allExams changes

  // Load popular certifications data (level-based)
  useEffect(() => {
    const loadPopularCertifications = async () => {
      try {
        // Q-Net 합격률 데이터 로드
        const passRatesData = await loadPassRatesData();
        if (passRatesData && examTypes.length > 0) {
          // 레벨별 TOP10 생성
          const levelData = {
            all: getPopularCertificationsByLevel(passRatesData, examTypes, 'all'),
            engineer: getPopularCertificationsByLevel(passRatesData, examTypes, 'engineer'),
            industrial: getPopularCertificationsByLevel(passRatesData, examTypes, 'industrial'),
            technician: getPopularCertificationsByLevel(passRatesData, examTypes, 'technician')
          };
          
          setLevelCertifications(levelData);
          setPopularCertifications(levelData.all); // 기본값은 전체
          console.log('🏆 레벨별 인기 자격증 데이터 로드 완료', levelData);
        }
      } catch (error) {
        console.error('❌ 인기 자격증 데이터 로드 실패:', error);
        // 폴백 데이터 사용
        const fallbackData = {
          all: getPopularCertificationsByLevel(null, examTypes, 'all'),
          engineer: getPopularCertificationsByLevel(null, examTypes, 'engineer'),
          industrial: getPopularCertificationsByLevel(null, examTypes, 'industrial'),
          technician: getPopularCertificationsByLevel(null, examTypes, 'technician')
        };
        setLevelCertifications(fallbackData);
        setPopularCertifications(fallbackData.all);
      }
    };

    // examTypes가 로드된 후에 인기 자격증 데이터 로드
    if (examTypes.length > 0) {
      loadPopularCertifications();
    }
  }, [examTypes]);

  // Handle level selection change
  useEffect(() => {
    if (levelCertifications[selectedLevel]) {
      setPopularCertifications(levelCertifications[selectedLevel]);
    }
  }, [selectedLevel, levelCertifications]);

  // Filter exams based on search and selected exam tag
  useEffect(() => {
    let filtered = examTypes;

    // Exam filter by tag selection
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(exam => 
        exam.key === selectedCategory || 
        exam.title.toLowerCase().includes(selectedCategory.toLowerCase())
      );
    }

    // Search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(exam => 
        exam.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exam.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exam.subjects?.some(subject => subject.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredExams(filtered);
  }, [examTypes, searchQuery, selectedCategory]);

  const handleExamClick = (examKey) => {
    navigate(`/${examKey}`);
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setSearchSuggestions([]);
    setIsSearchFocused(false);
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    // 입력박스 자체를 화면 최상단으로 스크롤
    const inputElement = document.querySelector('input[type="text"]');
    if (inputElement) {
      const inputRect = inputElement.getBoundingClientRect();
      const navHeight = 64; // 네비게이션 바 높이 (16 * 4)
      const scrollTop = window.pageYOffset + inputRect.top - navHeight;
      
      window.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
    }
  };

  const handleQuickSearch = (categoryOrKey) => {
    if (categoryOrKey === 'all') {
      setSearchQuery('');
      setSelectedCategory('all');
    } else {
      // Key로 카테고리 선택 (검색어는 비움)
      setSearchQuery('');
      setSelectedCategory(categoryOrKey);
      // 자격증 이름을 히스토리에 저장
      const exam = examTypes.find(e => e.key === categoryOrKey);
      if (exam) {
        saveToSearchHistory(exam.title);
      }
    }
    setIsSearchFocused(false);
  };

  const saveToSearchHistory = (query) => {
    if (!query.trim()) return;
    
    const newHistory = [query, ...searchHistory.filter(item => item !== query)].slice(0, 10);
    setSearchHistory(newHistory);
    localStorage.setItem('examSearchHistory', JSON.stringify(newHistory));

    const newRecent = [query, ...recentSearches.filter(item => item !== query)].slice(0, 5);
    setRecentSearches(newRecent);
    localStorage.setItem('recentExamSearches', JSON.stringify(newRecent));
  };

  const handleSearchSubmit = (query = searchQuery) => {
    if (query.trim()) {
      // 검색 기능 사용 기록
      autoFeedbackActions.recordFeatureUsage('search');
      gamificationActions.recordFeatureUsage('search');
      
      setSearchQuery(query);
      saveToSearchHistory(query);
      setIsSearchFocused(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'exam' && suggestion.examKey) {
      // Direct navigation to exam page
      navigate(`/${suggestion.examKey}`);
    } else {
      setSearchQuery(suggestion.title);
    }
    saveToSearchHistory(suggestion.title);
    setIsSearchFocused(false);
  };

  const startVoiceSearch = () => {
    // 음성 검색 기능 사용 기록
    autoFeedbackActions.recordFeatureUsage('voice_search');
    gamificationActions.recordFeatureUsage('voice_search');
    
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.lang = 'ko-KR';
      recognition.continuous = false;
      recognition.interimResults = false;

      setIsVoiceSearch(true);
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setSearchQuery(transcript);
        handleSearchSubmit(transcript);
        setIsVoiceSearch(false);
      };

      recognition.onerror = () => {
        setIsVoiceSearch(false);
      };

      recognition.onend = () => {
        setIsVoiceSearch(false);
      };

      recognition.start();
    }
  };

  const clearSearchHistory = () => {
    setSearchHistory([]);
    setRecentSearches([]);
    localStorage.removeItem('examSearchHistory');
    localStorage.removeItem('recentExamSearches');
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 relative overflow-hidden">
      {/* Mouse Follow Effect */}
      <div 
        className="fixed w-32 h-32 rounded-full pointer-events-none z-10 opacity-10 bg-orange-500 blur-xl transition-all duration-300"
        style={{
          left: mousePosition.x - 64,
          top: mousePosition.y - 64,
        }}
      />

      {/* Navigation */}
      <Header 
        setShowLoginPrompt={setShowLoginPrompt}
      />

      {/* Hero Section */}
      <section className="pt-24 pb-4 px-8 relative" id="search">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            
            <div className="mb-2">
              <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent animate-pulse">
                국가공인자격증
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="text-gray-900">자격증</span>
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent"> 검색</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              원하는 <span className="font-semibold text-orange-600">자격증을 검색</span>하고
              <br />
              <span className="font-semibold text-orange-600">실전 CBT 연습</span>을 시작하세요.
            </p>

            {/* Advanced Search Bar */}
            <div className="relative max-w-2xl mx-auto mb-8">
              {/* Animated Border Glow */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 opacity-20 animate-pulse blur-sm"></div>
              <div className="relative">
                <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
                  <MagnifyingGlassIcon className="w-6 h-6 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={handleSearchFocus}
                  onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                  placeholder="자격증명을 입력하세요 (예: 전기기사, 정보처리기사)"
                  className="w-full pl-5 pr-16 py-4 text-lg border-2 border-orange-300 rounded-2xl focus:border-orange-500 focus:outline-none transition-all duration-300 shadow-lg hover:shadow-xl focus:shadow-2xl relative bg-white focus:bg-orange-50 hover:border-orange-400"
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                  {isVoiceSearch && (
                    <div className="flex items-center space-x-2 text-orange-500">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-sm">듣는 중...</span>
                    </div>
                  )}
                  <button
                    onClick={startVoiceSearch}
                    disabled={isVoiceSearch}
                    className={`p-2 rounded-lg transition-colors ${
                      isVoiceSearch 
                        ? 'text-orange-500 bg-orange-100' 
                        : 'text-gray-400 hover:text-orange-500 hover:bg-orange-50'
                    }`}
                    title="음성 검색"
                  >
                    <MicrophoneIcon className="w-5 h-5" />
                  </button>
                  {searchQuery && (
                    <button
                      onClick={handleSearchClear}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Smart Suggestions Dropdown */}
              {isSearchFocused && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden backdrop-blur-lg bg-white/95">
                  <div className="max-h-96 overflow-y-auto">
                    
                    {/* Real-time Suggestions */}
                    {searchSuggestions.length > 0 && (
                      <div className="p-4">
                        <div className="flex items-center text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                          <SparklesIcon className="w-4 h-4 mr-2" />
                          실시간 검색 결과
                        </div>
                        {searchSuggestions.map((suggestion, index) => (
                          <button
                            key={`suggestion-${index}`}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="w-full text-left p-3 rounded-xl hover:bg-orange-50 transition-colors group flex items-center space-x-3"
                          >
                            <span className="text-2xl">{suggestion.icon}</span>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 group-hover:text-orange-600">
                                {suggestion.title}
                              </div>
                              <div className="text-sm text-gray-500">{suggestion.subtitle}</div>
                            </div>
                            <ChevronRightIcon className="w-4 h-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Trending Searches */}
                    {searchQuery === '' && trendingSearches.length > 0 && (
                      <div className="p-4 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <FireIcon className="w-4 h-4 mr-2 text-orange-500" />
                            실시간 인기 검색어
                          </div>
                          <div className="flex items-center text-xs text-orange-500">
                            <EyeIcon className="w-3 h-3 mr-1" />
                            실시간 업데이트
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {trendingSearches.map((term, index) => (
                            <button
                              key={`trending-${index}`}
                              onClick={() => handleSearchSubmit(term)}
                              className="text-left p-2 rounded-lg hover:bg-orange-50 transition-colors group flex items-center space-x-2"
                            >
                              <span className="text-sm font-bold text-orange-500 min-w-[20px]">
                                {index + 1}
                              </span>
                              <span className="text-gray-700 group-hover:text-orange-600 font-medium">
                                {term}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recent Searches */}
                    {searchQuery === '' && recentSearches.length > 0 && (
                      <div className="p-4 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <HistoryIcon className="w-4 h-4 mr-2" />
                            최근 검색
                          </div>
                          <button
                            onClick={clearSearchHistory}
                            className="text-xs text-orange-600 hover:text-orange-700"
                          >
                            전체 삭제
                          </button>
                        </div>
                        {recentSearches.map((term, index) => (
                          <button
                            key={`recent-${index}`}
                            onClick={() => handleSearchSubmit(term)}
                            className="w-full text-left p-2 rounded-lg hover:bg-gray-50 transition-colors group flex items-center space-x-3"
                          >
                            <HistoryIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700 group-hover:text-orange-600">
                              {term}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                  </div>
                </div>
              )}
            </div>

            {/* Quick Search Categories - Hidden when search is active */}
            {!searchQuery.trim() && examTypes.length > 0 && (
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <button
                  key="all"
                  onClick={() => handleQuickSearch('all')}
                  className={`px-3 py-2 sm:px-4 sm:py-2 md:px-5 md:py-3 lg:px-6 lg:py-3 rounded-full text-xs sm:text-sm md:text-sm lg:text-base font-medium transition-all duration-300 ${
                    selectedCategory === 'all'
                      ? 'bg-orange-500 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-orange-300 hover:text-orange-600'
                  }`}
                >
                  <span className="mr-2">📚</span>
                  전체
                </button>
                {examTypes.slice(0, 3).map((exam) => (
                  <button
                    key={exam.key}
                    onClick={() => handleQuickSearch(exam.key)}
                    className={`px-3 py-2 sm:px-4 sm:py-2 md:px-5 md:py-3 lg:px-6 lg:py-3 rounded-full text-xs sm:text-sm md:text-sm lg:text-base font-medium transition-all duration-300 ${
                      selectedCategory === exam.key
                        ? 'bg-orange-500 text-white shadow-lg scale-105'
                        : 'bg-white text-gray-600 border border-gray-200 hover:border-orange-300 hover:text-orange-600'
                    }`}
                  >
                    <span className="mr-2">
                      {exam.key.includes('electrical') ? '⚡' : 
                       exam.key.includes('fire') ? '🔥' : 
                       exam.key.includes('information') ? '💻' : 
                       exam.key.includes('safety') ? '🛡️' : '📜'}
                    </span>
                    {exam.title}
                  </button>
                ))}
              </div>
            )}

            {/* Search Results Summary - Appears when searching */}
            {searchQuery.trim() && (
              <div className="text-center mb-8">
                <div className="inline-flex items-center bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <span className="mr-2">🔍</span>
                  '{searchQuery}' 검색 결과 ({filteredExams.length}개)
                </div>
                
                {/* 자격증 추가 요청 버튼 - 검색 결과가 0개일 때만 표시 */}
                {filteredExams.length === 0 && (
                  <div className="flex justify-center">
                    <button
                      onClick={() => {
                        // 자격증 추가 요청을 피드백 시스템에 저장
                        const requestData = {
                          id: Date.now(),
                          timestamp: new Date().toISOString(),
                          type: 'exam_request',
                          examName: searchQuery.trim(),
                          page: window.location.pathname,
                          userAgent: navigator.userAgent,
                          user: user ? {
                            id: user.id,
                            name: user.name,
                            type: user.type
                          } : null
                        };

                        try {
                          // localStorage에 요청 데이터 저장
                          const existingRequests = JSON.parse(localStorage.getItem('examRequests') || '[]');
                          existingRequests.push(requestData);
                          localStorage.setItem('examRequests', JSON.stringify(existingRequests));

                          // 피드백 데이터에도 저장
                          const feedbackData = {
                            id: Date.now() + 1,
                            timestamp: new Date().toISOString(),
                            mood: 'neutral',
                            text: `"${searchQuery}" 자격증 추가를 요청했습니다.`,
                            type: 'exam_request',
                            page: window.location.pathname,
                            userAgent: navigator.userAgent
                          };

                          const existingFeedback = JSON.parse(localStorage.getItem('betaFeedback') || '[]');
                          existingFeedback.push(feedbackData);
                          localStorage.setItem('betaFeedback', JSON.stringify(existingFeedback));

                          alert(`"${searchQuery}" 자격증 추가 요청이 접수되었습니다.\n베타 기간 중 적극적으로 검토하여 업데이트 예정입니다.`);
                        } catch (error) {
                          console.error('Failed to save exam request:', error);
                          alert('요청 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
                        }
                      }}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-2xl font-semibold text-sm transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center space-x-3 group"
                    >
                      {/* Simple plus icon */}
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div className="w-3 h-0.5 bg-white"></div>
                        <div className="absolute w-0.5 h-3 bg-white"></div>
                      </div>
                      
                      <span>
                        <span className="text-orange-100">'{searchQuery}'</span> 자격증 추가 요청하기
                      </span>
                      
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Exam Grid */}
      <section className="px-8 pt-2 pb-6">
        <div className="max-w-7xl mx-auto">
          
          {filteredExams.length === 0 && !searchQuery.trim() ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">검색 결과가 없습니다</h3>
              <p className="text-gray-600 mb-6">다른 검색어를 시도해보거나 카테고리를 변경해보세요</p>
              
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
              >
                전체 자격증 보기
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {filteredExams.map((exam) => (
                <div
                  key={exam.key}
                  className="group p-8 rounded-2xl border-2 border-orange-500 bg-orange-50 transition-all duration-300 hover:shadow-lg cursor-pointer transform hover:scale-105"
                  onClick={() => handleExamClick(exam.key)}
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 group-hover:text-orange-600 transition-colors">
                    {exam.title}
                  </h3>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-500">문제 수</span>
                      <span className="font-semibold">{exam.totalQuestions}문제</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">시험 시간</span>
                      <span className="font-semibold">{exam.timeLimit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">합격 점수</span>
                      <span className="font-semibold text-orange-600">{exam.passingScore}</span>
                    </div>
                    {exam.statistics?.averagePassRate && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">평균 합격률</span>
                        <span className="font-semibold text-orange-600">{exam.statistics.averagePassRate}%</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mb-6">
                    <div className="text-sm text-gray-500 mb-2">출제 과목</div>
                    <div className="flex flex-wrap gap-1">
                      {exam.subjects?.slice(0, 4).map((subject, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {subject}
                        </span>
                      ))}
                      {exam.subjects?.length > 4 && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">
                          +{exam.subjects.length - 4}개
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <button className="w-full bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition-colors font-semibold group-hover:scale-105">
                    {exam.title} 선택
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 인기 자격증 랭킹 섹션 */}
      <section className="px-8 py-8 bg-gradient-to-r from-orange-50 to-orange-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              <span className="text-orange-600">인기 자격증</span> TOP 10
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              가장 많이 검색되고 응시하는 인기 자격증을 레벨별로 확인하고 바로 시작하세요
            </p>
          </div>

          {/* 레벨 탭 메뉴 */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-xl p-2 shadow-md border border-orange-200">
              <div className="flex space-x-2">
                {[
                  { key: 'all', label: '전체', icon: '🎯' },
                  { key: 'engineer', label: '기사', icon: '🏗️' },
                  { key: 'industrial', label: '산업기사', icon: '⚙️' },
                  { key: 'technician', label: '기능사', icon: '🔧' }
                ].map((level) => (
                  <button
                    key={level.key}
                    onClick={() => setSelectedLevel(level.key)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center space-x-2 ${
                      selectedLevel === level.key
                        ? 'bg-orange-500 text-white shadow-md'
                        : 'text-gray-600 hover:bg-orange-100 hover:text-orange-700'
                    }`}
                  >
                    <span>{level.icon}</span>
                    <span>{level.label}</span>
                    {levelCertifications[level.key] && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        selectedLevel === level.key
                          ? 'bg-orange-300 text-orange-800'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {levelCertifications[level.key].length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 현재 레벨 정보 표시 */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center bg-white rounded-lg px-4 py-2 shadow-sm border border-orange-200">
              <span className="text-gray-600 text-sm">현재 보기:</span>
              <span className="ml-2 font-semibold text-orange-600">
                {selectedLevel === 'all' && '전체 레벨'}
                {selectedLevel === 'engineer' && '기사급 (대학교 졸업 수준)'}
                {selectedLevel === 'industrial' && '산업기사급 (전문대 졸업 수준)'}
                {selectedLevel === 'technician' && '기능사급 (고등학교 졸업 수준)'}
              </span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {popularCertifications.map((cert, index) => {
              // Q-Net 슬러그를 사용하되, CBT 지원이면 기존 key 우선 사용
              const displayKey = cert.examExists ? cert.key : createSlugFromName(cert.title);
              // 고유한 key 생성 (레벨 + 순위 + 제목)
              const uniqueKey = `${selectedLevel}-${cert.rank || index}-${cert.key || cert.title}`;
              
              return (
                <div
                  key={uniqueKey}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-4 border border-orange-200 cursor-pointer group"
                  onClick={() => navigate(`/${displayKey}`)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{cert.icon}</span>
                      <div className="text-2xl font-bold text-orange-500">#{cert.rank}</div>
                    </div>
                    <div className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full font-medium">
                      {cert.growth}
                    </div>
                  </div>
                  
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors text-sm">
                    {cert.title}
                  </h3>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex flex-col">
                      <span>{cert.isCBTSupported ? 'CBT 지원' : '정보 제공'}</span>
                      {cert.avgApplicants && (
                        <span className="text-orange-500">평균 {cert.avgApplicants.toLocaleString()}명</span>
                      )}
                    </div>
                    <ChevronRightIcon className="w-3 h-3 group-hover:text-orange-500 transition-colors" />
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="text-center">
            <div className="bg-white rounded-xl p-6 max-w-4xl mx-auto shadow-md border border-orange-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                🏆 인기 자격증 선택의 이유
              </h3>
              <div className="grid md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-2xl mb-2">📈</div>
                  <div className="font-medium text-gray-900">높은 취업률</div>
                  <div className="text-gray-600">취업 시장에서 높은 가치</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-2">💰</div>
                  <div className="font-medium text-gray-900">연봉 향상</div>
                  <div className="text-gray-600">자격증 수당 및 승진 기회</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-2">🎯</div>
                  <div className="font-medium text-gray-900">실무 활용</div>
                  <div className="text-gray-600">현장에서 바로 쓰이는 지식</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-2">⭐</div>
                  <div className="font-medium text-gray-900">안정적 수요</div>
                  <div className="text-gray-600">지속적인 시장 수요</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-8 bg-orange-500 text-white" id="stats">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              24시간 편의 서비스
            </h2>
            <p className="text-xl text-orange-100 max-w-3xl mx-auto">
              편의점처럼 언제나 열려있는 학습 환경으로 여러분의 합격을 도와드립니다
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{filteredExams.length}+</div>
              <div className="text-sm text-orange-100">지원 자격증</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">2,847</div>
              <div className="text-sm text-orange-100">총 문제 수</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">15,420</div>
              <div className="text-sm text-orange-100">누적 이용자</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">87.5%</div>
              <div className="text-sm text-orange-100">만족도</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">C</span>
                </div>
                <span className="text-xl font-bold">CBT 편의점</span>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                편리하고 효율적인 CBT 연습 서비스로 여러분의 합격을 도와드립니다.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">제공 시험</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-orange-400 transition-colors">전기기사</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">정보처리기사</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">건축기사</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">기타 기사급</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">학습 도구</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-orange-400 transition-colors">기출문제</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">모의고사</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">오답노트</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">학습통계</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">고객지원</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-orange-400 transition-colors">이용안내</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">문의하기</a></li>
                <li><a href="/privacy" className="hover:text-orange-400 transition-colors">개인정보처리방침</a></li>
              </ul>
            </div>
            
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © 2024 CBT 편의점. 언제 어디서나 편리한 CBT 연습 서비스.
            </p>
          </div>
        </div>
      </footer>

      {/* 로그인 프롬프트 모달 */}
      {showLoginPrompt && (
        <LoginPromptManager
          userProgress={{
            solvedQuestions: 0,
            wrongAnswers: 0,
            sessionMinutes: 0
          }}
          isLoggedIn={isAuthenticated}
          onLogin={async (provider) => {
            try {
              await loginWithSocial(provider);
              setShowLoginPrompt(false);
            } catch (error) {
              console.error('Login failed:', error);
            }
          }}
          onClose={() => setShowLoginPrompt(false)}
          forceShow={showLoginPrompt}
          isDiagnosticMode={false}
        />
      )}

    </div>
  );
};

export default ExamSearchSelection;